import { Link, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorState } from '@/components/shared/ErrorState';
import { useAuth } from '@/contexts/AuthContext';

/** In-shell 404 (F064 FR4) — rendered inside `AppLayout` so the owner keeps
 * sidebar/header and an obvious way out. */
export function NotFoundPage() {
  const { t } = useTranslation();
  return (
    <div className="p-6">
      <ErrorState
        variant="not-found"
        cta={
          <Button asChild variant="outline">
            <Link to="/">{t('common.notFound.goHome')}</Link>
          </Button>
        }
      />
    </div>
  );
}

/** Bare 404 for unauthenticated visitors — no shell chrome, link to /login. */
export function PublicNotFoundPage() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="w-full max-w-md">
        <ErrorState
          variant="not-found"
          cta={
            <Button asChild variant="outline">
              <Link to="/login">{t('common.notFound.goLogin')}</Link>
            </Button>
          }
        />
      </div>
    </div>
  );
}

/**
 * Route gate for the `*` catch-all (F064 decision 2): authenticated → Outlet
 * into the chrome'd shell 404; unauthenticated → bare 404 with a /login link.
 * Unlike `ProtectedRoute` this never redirects — an unknown URL is told the
 * truth, not silently bounced.
 */
export function NotFoundGate() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="p-8 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <PublicNotFoundPage />;
  }

  return <Outlet />;
}
